import { useEffect, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function Products({ forceShow }) {
  const { setCrumbs } = useOutletContext();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [products, setProducts] = useState(null);

  useEffect(() => { setCrumbs([]); }, [setCrumbs]);

  useEffect(() => {
    // A learner with exactly one product skips the picker (§2), unless they asked for it.
    if (!forceShow && user && Array.isArray(user.products) && user.products.length === 1) {
      navigate(`/${user.products[0]}`, { replace: true });
      return;
    }
    api.get("/catalog").then((res) => setProducts(res.data.products));
  }, [forceShow, user, navigate]);

  if (!products) return <div className="loading">Loading products…</div>;

  return (
    <>
      <section className="hero on-dark" data-testid="products-hero">
        <div className="hero__inner">
          <div className="eyebrow">Included with every EQUIPR subscription</div>
          <h1 className="hero__title">Learn your role in the product you actually use.</h1>
          <p className="hero__sub">
            Pick your product, then your role. Short video modules follow the real workflows, a knowledge check closes each section, and the final assessment certifies you.
          </p>
          <div className="grid-products" data-testid="product-grid">
            {products.map((p) => (
              <button
                key={p.id}
                className={`pcard on-dark ${p.live ? "pcard--live" : "pcard--locked"}`}
                onClick={() => p.live && navigate(`/${p.id}`)}
                disabled={!p.live}
                aria-disabled={!p.live}
                data-testid={`product-card-${p.id}`}
              >
                <div className="pcard__top">
                  <span className={`tag ${p.live ? "tag--live" : "tag--muted"}`}>{p.tag}</span>
                  <span className={`status ${p.live ? "status--live" : "status--muted"}`}>
                    {p.status === "live" ? "Live" : p.status === "production" ? "In production" : "Planned"}
                  </span>
                </div>
                <div className="pcard__name">{p.name}</div>
                <p className="pcard__blurb">{p.blurb}</p>
                <div className="pcard__chips">
                  {p.chips.map((c, i) => <span key={i} className="chip chip--dark">{c}</span>)}
                </div>
                <div className={`pcard__cta ${p.live ? "pcard__cta--live" : "pcard__cta--locked"}`}>
                  {p.live ? "Choose your role →" : "Notify me"}
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="page" data-testid="products-info">
        <div className="container grid-cream">
          <div className="icard">
            <div className="eyebrow">In-person</div>
            <div className="icard__title">Get your whole team running</div>
            <p className="icard__body">Paid onsite sessions per role, built on your own jobs, price lists and integration. Quoted per product engagement, with the Academy path as pre-work.</p>
          </div>
          <div className="icard">
            <div className="eyebrow">Help Center</div>
            <div className="icard__title">Just need one answer?</div>
            <p className="icard__body">The Academy teaches a role start to finish. The Help Center answers a single task and stays where it is, per product.</p>
          </div>
          <div className="icard icard--dashed">
            <div className="eyebrow">Adding a product</div>
            <div className="icard__title">Five boxes, then a tile</div>
            <p className="icard__body">Written guide exists, roles named, essential workflows flagged, demo data seeded, content owner assigned. Products stay fully separate.</p>
          </div>
        </div>
      </section>
    </>
  );
}
